/**
 * AuthImg — renders an <img> whose bytes come from an authenticated
 * fetch. The browser's native <img> tag can only attach cookies
 * (not localStorage Bearer tokens), so admin-only image endpoints
 * silently 401 in production for users whose session cookie was
 * dropped (cross-domain, cleared, expired) even though they're still
 * "logged in" via the JWT in localStorage.
 *
 * This component:
 *   1. Fetches the URL with `apiFetch`'s auth-header rules (Bearer
 *      from localStorage + credentials: include).
 *   2. Converts to a blob URL + sets it as <img src>.
 *   3. Surfaces a real error state instead of the browser's broken-
 *      image icon (so "auto-detect generates broken images" becomes
 *      a clear error message about which step failed).
 *   4. Cleans up blob URLs on unmount / re-fetch.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, ImageOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AuthImg = ({ url, alt, className, style, fallback, onLoaded, ...rest }) => {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let createdObjectUrl = null;
    setLoading(true);
    setError(null);
    setSrc(null);

    (async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const fullUrl = url.startsWith('http') ? url : `${API}${url}`;
        const res = await fetch(fullUrl, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (cancelled) return;
        if (!res.ok) {
          // Show the backend's actual reason — "Source file not found"
          // tells the admin to re-upload, "401" tells them to log back in.
          let detail = `HTTP ${res.status}`;
          try {
            const j = await res.json();
            if (j?.detail) detail = j.detail;
          } catch {
            /* not JSON — keep HTTP code */
          }
          throw new Error(detail);
        }
        const blob = await res.blob();
        createdObjectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(createdObjectUrl);
          return;
        }
        setSrc(createdObjectUrl);
        setLoading(false);
        onLoaded?.(blob);
      } catch (e) {
        if (cancelled) return;
        setError(e.message || 'Failed to load');
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (createdObjectUrl) URL.revokeObjectURL(createdObjectUrl);
    };
    // onLoaded omitted on purpose — inline callbacks would re-fetch every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${className || ''}`} style={style}>
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error) {
    if (fallback) return fallback({ error });
    return (
      <div className={`flex flex-col items-center justify-center text-[10px] text-red-500/80 ${className || ''}`} style={style} title={error}>
        <ImageOff className="h-4 w-4 mb-0.5 opacity-70" />
        <span className="truncate max-w-full px-1">{error}</span>
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} style={style} {...rest} />;
};

export default AuthImg;
